import { useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Plus, SendHorizontal } from 'lucide-react';

import {
  useChatAttachmentUploads,
  useComposerAutosize,
} from '@/components/agent-chat';
import { ChatAttachments } from '@/components/agents/chat-attachments';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ROUTES } from '@/constants/routes';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { cn } from '@/lib/utils';
import { useOrgStore } from '@/stores/org-store';

export function NewChatPromptBar({ agentId }: { agentId: string }) {
  const navigate = useOrgNavigate();
  const orgId = useOrgStore((s) => s.currentOrg?.orgid);

  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    attachments,
    addFiles,
    removeAttachment,
    uploadedAttachments,
    isUploading,
  } = useChatAttachmentUploads(orgId, agentId);

  useComposerAutosize(textareaRef, input);

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: addFiles,
    noClick: true,
    noKeyboard: true,
    multiple: true,
  });

  const sendDisabled =
    (!input.trim() && uploadedAttachments.length === 0) || isUploading;

  // The live composer lives on the chat surface: the draft and the already
  // uploaded file refs ride along in router state, and the chat page sends
  // them on mount.
  const handleSubmit = () => {
    if (sendDisabled) return;
    navigate(ROUTES.CHAT_HOME.ROOT, {
      state: {
        chatDraft: input.trim(),
        autoSend: true,
        attachments: uploadedAttachments.map((a) => ({
          fileId: a.fileId,
          filename: a.filename,
        })),
      },
    });
  };

  return (
    <div
      {...getRootProps()}
      className={cn(
        'flex w-full flex-col gap-1 rounded-xl bg-accent px-2 py-1 transition-colors lg:w-2/3',
        'focus-within:ring-1 focus-within:ring-ring/40',
        isDragActive && 'ring-1 ring-primary/60',
      )}
    >
      <ChatAttachments attachments={attachments} onRemove={removeAttachment} />
      <div className='flex items-end gap-1'>
        <input
          ref={fileInputRef}
          type='file'
          multiple
          className='hidden'
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
        <Button
          variant='ghost'
          size='icon'
          className='size-9 shrink-0 hover:bg-background/40'
          aria-label='Attach files'
          onClick={() => fileInputRef.current?.click()}
        >
          <Plus className='size-4' />
        </Button>
        <Textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          rows={1}
          placeholder={
            isDragActive ? 'Drop files to attach them' : 'Start a new chat'
          }
          aria-label='Start a new chat'
          className='min-h-9 flex-1 resize-none border-none bg-transparent px-3 py-2 text-base shadow-none focus-visible:ring-0 dark:bg-transparent'
        />
        <Button
          size='icon'
          variant={sendDisabled ? 'ghost' : 'default'}
          className='size-9 shrink-0'
          aria-label='Start chat'
          disabled={sendDisabled}
          onClick={handleSubmit}
        >
          <SendHorizontal className='size-4' />
        </Button>
      </div>
    </div>
  );
}
